import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUp } from "lucide-react";
import { sendClick } from "@/lib/tracking";
import { pushDL } from "@/lib/analytics";
import { useLang } from "@/context/LanguageContext";

/**
 * Floating stack sitting to the SIDE of the AI Support Agent chat launcher.
 * - WhatsApp: always visible, opens a chat to +971 56 300 3311.
 * - Back-to-top arrow: fades in once the user has scrolled past ~400px,
 *   pinned directly above WhatsApp.
 */
export default function FloatingActions({ isRTL = false }) {
  const [showTop, setShowTop] = useState(false);
  const isAR = useLang().lang === "ar";

  useEffect(() => {
    const on = () => setShowTop(window.scrollY > 400);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const scrollUp = () => {
    try { window.scrollTo({ top: 0, behavior: "smooth" }); }
    catch { window.scrollTo(0, 0); }
  };

  const desktopSide = isRTL ? "sm:right-8 sm:left-auto" : "sm:left-8 sm:right-auto";
  const mobileSide = "right-6";

  const onWhatsAppClick = () => {
    sendClick("whatsapp");
    pushDL("whatsapp_click", { source: "floating" });
  };

  return (
    <div
      data-testid="floating-actions"
      style={{ zIndex: 45 }}
      className={`fixed bottom-24 sm:bottom-8 ${mobileSide} ${desktopSide} flex flex-col items-center gap-3 pointer-events-none`}
    >
      <AnimatePresence>
        {showTop && (
          <motion.button
            key="back-to-top"
            type="button"
            onClick={scrollUp}
            data-testid="back-to-top-btn"
            aria-label={isAR ? "العودة إلى أعلى الصفحة" : "Back to top"}
            initial={{ opacity: 0, scale: 0.7, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.7, y: 8 }}
            transition={{ duration: 0.22 }}
            className="pointer-events-auto w-12 h-12 rounded-full bg-propertic-navy text-propertic-paper border border-propertic-gold/40 shadow-xl grid place-items-center hover:bg-propertic-gold hover:text-propertic-navy transition-colors"
          >
            <ArrowUp className="w-4 h-4" strokeWidth={2.2} />
          </motion.button>
        )}
      </AnimatePresence>

      {/* WhatsApp (desktop). Same number. */}
      <a
        href="https://wa.me/971563003311?text=Hi%20Propertic%20%E2%80%94%20I%27d%20like%20to%20chat%20about%20Dubai%20real%20estate."
        target="_blank"
        rel="noreferrer noopener"
        onClick={onWhatsAppClick}
        data-testid="floating-whatsapp-btn"
        aria-label={isAR ? "تواصل معنا عبر واتساب" : "Chat with us on WhatsApp"}
        style={{ backgroundColor: "#25D366", color: "#25D366", "--halo-delay": "0s" }}
        className="halo-pulse pointer-events-auto hidden sm:grid w-12 h-12 rounded-full place-items-center shadow-xl transition-transform hover:scale-105"
      >
        <svg viewBox="0 0 32 32" width="20" height="20" aria-hidden="true">
          <path fill="#fff" d="M19.11 17.26c-.28-.14-1.63-.8-1.88-.9-.25-.09-.44-.14-.62.14-.18.28-.71.9-.87 1.08-.16.18-.32.2-.6.07-.28-.14-1.16-.43-2.21-1.37-.82-.73-1.37-1.63-1.53-1.91-.16-.28-.02-.43.12-.57.13-.13.28-.32.42-.48.14-.16.19-.28.28-.46.09-.18.05-.35-.02-.48-.07-.14-.62-1.49-.85-2.03-.22-.53-.45-.46-.62-.47-.16-.01-.35-.01-.53-.01-.18 0-.48.07-.72.35-.25.28-.94.92-.94 2.25 0 1.32.96 2.6 1.09 2.78.14.18 1.89 2.88 4.57 4.04.64.28 1.14.44 1.53.57.64.2 1.22.17 1.68.1.51-.08 1.63-.66 1.86-1.31.23-.65.23-1.2.16-1.31-.07-.11-.25-.18-.53-.32zM16 3C8.82 3 3 8.82 3 16c0 2.5.73 4.85 1.99 6.83L3 29l6.34-1.98A12.95 12.95 0 0 0 16 29c7.18 0 13-5.82 13-13S23.18 3 16 3zm0 23.5c-2.09 0-4.03-.58-5.7-1.58l-.41-.24-3.76 1.17 1.19-3.66-.27-.42A10.5 10.5 0 1 1 26.5 16C26.5 21.79 21.79 26.5 16 26.5z" />
        </svg>
      </a>
    </div>
  );
}
